import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { Compass, Map, FileText, TrendingUp, AlertTriangle, Lightbulb, ArrowRight, Loader2, ChevronDown, ChevronUp, Layers, Globe, Shield, Target, Zap, Sparkles, BarChart3, Eye } from "lucide-react";
import { trackEvent } from "../../lib/track";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MATURITY_COLORS = { emerging: "#f59e0b", growing: "#3b82f6", mature: "#10b981" };
const IMPACT_ICONS = { efficiency: Zap, equity: Shield, safety: AlertTriangle, creativity: Lightbulb, sustainability: Globe };
const SEVERITY_COLORS = { high: "#ef4444", medium: "#f59e0b", low: "#3b82f6" };

/* ─── Animated Counter ─── */
const AnimatedCounter = ({ value, suffix = "", decimals = 0, duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (!value && value !== 0) return;
    const target = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(target)) { setDisplay(value); return; }
    let start = 0;
    const startTime = performance.now();
    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay((eased * target).toFixed(decimals));
      if (progress < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(ref.current);
  }, [value, duration, decimals]);
  return <>{display}{suffix}</>;
};

/* ─── Animated Bar ─── */
const AnimatedBar = ({ value, max, color, delay = 0 }) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(Math.min((value / Math.max(max, 1)) * 100, 100)), 100 + delay);
    return () => clearTimeout(t);
  }, [value, max, delay]);
  return (
    <div className="relative w-full h-7 rounded-md overflow-hidden bg-white/[0.03]">
      <div className="absolute inset-0 h-full rounded-md transition-all ease-out"
        style={{ width: `${width}%`, background: `linear-gradient(90deg, ${color}20, ${color}50)`, transitionDuration: "1s", transitionDelay: `${delay}ms` }} />
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white/90 z-10">
        {value}%
      </span>
    </div>
  );
};

/* ─── Scroll Reveal Wrapper ─── */
const Reveal = ({ children, delay = 0, className = "" }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(24px)", transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms` }}>
      {children}
    </div>
  );
};

/* ─── Mini Radar Chart (SVG) ─── */
const RadarChart = ({ data, size = 200 }) => {
  const dims = ["care_ratio", "diversity", "geo_spread", "density"];
  const labels = ["Care", "Diversity", "Spread", "Density"];
  const colors = ["#10b981", "#ec4899", "#6366f1", "#f59e0b"];
  const cx = size / 2, cy = size / 2, r = size * 0.38;
  const angleStep = (2 * Math.PI) / dims.length;

  const getPoint = (dimIdx, value, maxR) => {
    const angle = -Math.PI / 2 + dimIdx * angleStep;
    const norm = Math.min(value / 100, 1);
    return { x: cx + norm * maxR * Math.cos(angle), y: cy + norm * maxR * Math.sin(angle) };
  };

  const polygon = dims.map((d, i) => {
    const p = getPoint(i, data[d] || 0, r);
    return `${p.x},${p.y}`;
  }).join(" ");

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {[0.25, 0.5, 0.75, 1].map((s) => (
        <polygon key={`ring-${s}`} points={dims.map((_, j) => { const p = getPoint(j, s * 100, r); return `${p.x},${p.y}`; }).join(" ")}
          fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
      ))}
      {dims.map((d, i) => {
        const p = getPoint(i, 100, r);
        return <line key={`axis-${d}`} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />;
      })}
      <polygon points={polygon} fill="rgba(99,102,241,0.15)" stroke="#6366f1" strokeWidth="2" className="transition-all duration-1000" />
      {dims.map((d, i) => {
        const p = getPoint(i, data[d] || 0, r);
        const lp = getPoint(i, 115, r);
        return (
          <g key={`node-${d}`}>
            <circle cx={p.x} cy={p.y} r="3.5" fill={colors[i]} stroke="#0c0a1e" strokeWidth="2" />
            <text x={lp.x} y={lp.y} fill={colors[i]} fontSize="9" fontWeight="700" textAnchor="middle" dominantBaseline="middle">{labels[i]}</text>
          </g>
        );
      })}
    </svg>
  );
};

/* ─── Confidence Gauge ─── */
const ConfidenceGauge = ({ value, delay = 0 }) => {
  const [fill, setFill] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setFill(value), 200 + delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  const circumference = 2 * Math.PI * 18;
  const offset = circumference - (fill / 100) * circumference;
  const color = value >= 85 ? "#10b981" : value >= 70 ? "#3b82f6" : "#f59e0b";
  return (
    <div className="relative w-12 h-12 flex-shrink-0">
      <svg className="w-12 h-12 -rotate-90" viewBox="0 0 44 44">
        <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
        <circle cx="22" cy="22" r="18" fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1.2s ease-out" }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-black" style={{ color }}>{fill}%</span>
    </div>
  );
};


/* ─── Cross-Industry AI Readiness Matrix (B) ─── */
const ReadinessMatrix = ({ matrixData, onSelectIndustry }) => {
  const [sortBy, setSortBy] = useState("total");
  const [hoveredRow, setHoveredRow] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const dimensions = [
    { key: "care_ratio", label: "Care %", color: "#10b981" },
    { key: "diversity", label: "Diversity %", color: "#ec4899" },
    { key: "geo_spread", label: "Geo Spread", color: "#6366f1" },
    { key: "density", label: "AI Density", color: "#f59e0b" },
    { key: "extraction_ratio", label: "Extraction %", color: "#ef4444" },
  ];

  const sorted = useMemo(() => {
    if (!matrixData?.industries) return [];
    return [...matrixData.industries].sort((a, b) => {
      if (sortBy === "total") return b.total - a.total;
      return (b[sortBy] || 0) - (a[sortBy] || 0);
    });
  }, [matrixData, sortBy]);

  if (!matrixData?.industries?.length) return null;

  const maxVals = dimensions.reduce((acc, d) => {
    acc[d.key] = Math.max(...sorted.map(i => i[d.key] || 0), 1);
    return acc;
  }, {});

  return (
    <Reveal>
      <div className="rounded-2xl overflow-hidden" data-testid="readiness-matrix"
        style={{ background: "linear-gradient(135deg, #0c0a1e 0%, #0f1629 50%, #0a1020 100%)", border: "1px solid rgba(99,102,241,0.12)" }}>
        <div className="p-5 md:p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Map className="w-4 h-4 text-indigo-400" />
              <p className="text-[10px] font-bold text-indigo-400/70 uppercase tracking-[0.2em]">Cross-Industry AI Readiness Matrix</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[9px] text-slate-500 hidden sm:block">Click a row to explore</span>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} data-testid="matrix-sort"
                className="px-3 py-1.5 bg-black/40 border border-white/[0.08] rounded-lg text-[10px] text-white">
                <option value="total">Sort by Size</option>
                {dimensions.map(d => <option key={d.key} value={d.key}>Sort by {d.label}</option>)}
              </select>
            </div>
          </div>

          {/* Header */}
          <div className="grid grid-cols-[200px_repeat(5,1fr)_80px] gap-1 mb-2 px-1">
            <span className="text-[9px] text-slate-500 font-bold uppercase">Industry</span>
            {dimensions.map(d => (
              <span key={d.key} className="text-[9px] text-center font-bold uppercase cursor-pointer hover:opacity-80 transition-opacity"
                style={{ color: d.color }} onClick={() => setSortBy(d.key)}>{d.label}</span>
            ))}
            <span className="text-[9px] text-slate-500 font-bold uppercase text-right">Total</span>
          </div>

          {/* Rows */}
          <div className="space-y-1">
            {sorted.map((ind, i) => {
              const isHovered = hoveredRow === ind.industry;
              const isSelected = selectedRow === ind.industry;
              return (
                <Reveal key={ind.industry} delay={i * 40}>
                  <div
                    className="rounded-xl overflow-hidden cursor-pointer transition-all duration-300"
                    style={{
                      background: isSelected ? "rgba(99,102,241,0.08)" : isHovered ? "rgba(255,255,255,0.03)" : "transparent",
                      border: isSelected ? "1px solid rgba(99,102,241,0.25)" : "1px solid transparent",
                    }}
                    onMouseEnter={() => setHoveredRow(ind.industry)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={() => {
                      setSelectedRow(isSelected ? null : ind.industry);
                      if (!isSelected) onSelectIndustry?.(ind.industry);
                    }}
                  >
                    <div className="grid grid-cols-[200px_repeat(5,1fr)_80px] gap-1 items-center px-2 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-500 w-4 text-right">{i + 1}</span>
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 transition-transform duration-300"
                          style={{ background: ind.color, transform: isHovered ? "scale(1.4)" : "scale(1)" }} />
                        <span className="text-[10px] text-slate-300 truncate font-medium">{ind.industry}</span>
                        {isHovered && <ArrowRight className="w-3 h-3 text-indigo-400 animate-pulse flex-shrink-0" />}
                      </div>
                      {dimensions.map((d, di) => (
                        <div key={d.key}>
                          <AnimatedBar value={ind[d.key]} max={maxVals[d.key]} color={d.color} delay={i * 40 + di * 60} />
                        </div>
                      ))}
                      <span className="text-[10px] font-bold text-slate-400 text-right">{ind.total.toLocaleString()}</span>
                    </div>

                    {/* Expanded radar preview */}
                    {isSelected && (
                      <div className="px-4 pb-4 pt-1 flex items-center gap-6 animate-fade-in border-t border-white/[0.04]">
                        <RadarChart data={ind} size={140} />
                        <div className="flex-1 grid grid-cols-2 sm:grid-cols-5 gap-3">
                          <div><p className="text-lg font-black text-emerald-400"><AnimatedCounter value={ind.care_ratio} suffix="%" decimals={1} /></p><p className="text-[9px] text-slate-500">Care-Oriented</p></div>
                          <div><p className="text-lg font-black text-pink-400"><AnimatedCounter value={ind.diversity} suffix="%" decimals={1} /></p><p className="text-[9px] text-slate-500">Diverse Founders</p></div>
                          <div><p className="text-lg font-black text-indigo-400"><AnimatedCounter value={ind.num_countries} /></p><p className="text-[9px] text-slate-500">Countries</p></div>
                          <div><p className="text-lg font-black text-amber-400"><AnimatedCounter value={ind.software} /></p><p className="text-[9px] text-slate-500">Software</p></div>
                          <div><p className="text-lg font-black text-cyan-400"><AnimatedCounter value={ind.research_papers || 0} /></p><p className="text-[9px] text-slate-500">Research Papers</p></div>
                          {ind.research_care_ratio > 0 && (
                            <div><p className="text-lg font-black text-purple-400"><AnimatedCounter value={ind.research_care_ratio} suffix="%" decimals={1} /></p><p className="text-[9px] text-slate-500">Research Care %</p></div>
                          )}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); onSelectIndustry?.(ind.industry); }}
                          className="flex items-center gap-2 px-4 py-2 bg-indigo-600/30 hover:bg-indigo-600/50 border border-indigo-500/20 rounded-lg text-[10px] text-indigo-300 font-bold transition-all whitespace-nowrap">
                          <Compass className="w-3 h-3" /> Generate Report
                        </button>
                      </div>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 mt-4 pt-3 border-t border-white/[0.04]">
            <span className="text-[9px] text-slate-500">Low</span>
            <div className="flex gap-0.5">
              {[0.08, 0.2, 0.35, 0.5, 0.63].map((op) => (
                <div key={`legend-${op}`} className="w-6 h-3 rounded-sm" style={{ background: `rgba(99, 102, 241, ${op})` }} />
              ))}
            </div>
            <span className="text-[9px] text-slate-500">High</span>
            <span className="text-[9px] text-slate-500 ml-4">{matrixData.total?.toLocaleString()} total entities</span>
          </div>
        </div>
      </div>
    </Reveal>
  );
};


/* ─── Industry Futures Compass (A + D) ─── */
const FuturesCompass = ({ industries, compassRef, autoIndustry }) => {
  const [selected, setSelected] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedArea, setExpandedArea] = useState(null);
  const [activeSection, setActiveSection] = useState("areas");

  useEffect(() => {
    if (autoIndustry && autoIndustry !== selected) {
      setSelected(autoIndustry);
      generate(autoIndustry);
    }
  }, [autoIndustry]);

  const generate = async (ind) => {
    const industry = ind || selected;
    if (!industry || loading) return;
    setSelected(industry);
    setLoading(true);
    setData(null);
    setExpandedArea(null);
    setActiveSection("areas");
    trackEvent("industry_futures", "industries", industry);
    try {
      const r = await axios.post(`${API}/industry/futures`, { industry }, { timeout: 90000 });
      setData(r.data);
    } catch {
      setData({ error: "Failed to generate futures analysis. Please try again." });
    }
    setLoading(false);
  };

  const sectionTabs = [
    { id: "areas", label: "Focus Areas", icon: Target, count: data?.top_10_focus_areas?.length },
    { id: "spaces", label: "White Spaces", icon: Lightbulb, count: data?.white_spaces?.length },
    { id: "convergence", label: "Convergence", icon: Layers, count: data?.convergence_opportunities?.length },
    { id: "risks", label: "Risks", icon: AlertTriangle, count: data?.risk_signals?.length },
    { id: "reports", label: "Reports", icon: FileText, count: data?.report_references?.length },
  ];

  return (
    <Reveal delay={200}>
      <div ref={compassRef} className="rounded-2xl overflow-hidden" data-testid="futures-compass"
        style={{ background: "linear-gradient(135deg, #0c0a1e 0%, #0f1629 50%, #0a1020 100%)", border: "1px solid rgba(16,185,129,0.12)" }}>
        <div className="p-5 md:p-6">
          <div className="flex items-center gap-2 mb-2">
            <Compass className="w-4 h-4 text-emerald-400" />
            <p className="text-[10px] font-bold text-emerald-400/70 uppercase tracking-[0.2em]">Industry Futures Compass</p>
          </div>
          <p className="text-sm text-slate-400 mb-5">Select an industry to discover the Top 10 AI focus areas, white spaces, convergence opportunities, and intelligence from major industry reports.</p>

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <select value={selected} onChange={e => setSelected(e.target.value)} data-testid="futures-industry-select"
              className="flex-1 px-4 py-3 bg-black/40 border border-white/[0.08] rounded-xl text-sm text-white focus:outline-none focus:border-emerald-500/30 transition-colors">
              <option value="">Choose an industry...</option>
              {industries.map(ind => (
                <option key={ind.industry} value={ind.industry}>{ind.industry} ({ind.count.toLocaleString()})</option>
              ))}
            </select>
            <button onClick={() => generate()} disabled={!selected || loading} data-testid="futures-generate-btn"
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 disabled:opacity-30 text-white text-sm font-bold rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? "Analyzing..." : "Generate Futures Report"}
            </button>
          </div>

          {/* Loading Animation */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="relative">
                <div className="w-20 h-20 rounded-full border-2 border-emerald-400/10 animate-ping absolute inset-0" />
                <div className="w-20 h-20 rounded-full border-2 border-emerald-400/30 border-t-emerald-400 animate-spin" />
                <Compass className="w-6 h-6 text-emerald-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <div className="text-center mt-2">
                <p className="text-sm text-white font-medium">Analyzing {selected}</p>
                <p className="text-[10px] text-slate-500 mt-1 animate-pulse">Cross-referencing observatory data with industry reports...</p>
              </div>
              <div className="flex gap-1.5 mt-2">
                {[0, 1, 2, 3, 4].map(i => (
                  <div key={`pulse-dot-${i}`} className="w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                ))}
              </div>
            </div>
          )}

          {data && !data.error && (
            <div className="space-y-5">
              {/* Executive Summary */}
              <Reveal>
                <div className="bg-emerald-500/[0.06] border border-emerald-500/15 rounded-xl p-5 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/[0.04] rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
                  <h3 className="text-lg font-bold text-white mb-2">{data.title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{data.executive_summary}</p>
                  {data.stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4 pt-4 border-t border-emerald-500/10">
                      <div className="text-center"><p className="text-xl font-black text-emerald-400"><AnimatedCounter value={data.stats.total} /></p><p className="text-[9px] text-slate-500">AI Entities</p></div>
                      <div className="text-center"><p className="text-xl font-black text-emerald-400"><AnimatedCounter value={data.stats.care_pct} suffix="%" decimals={1} /></p><p className="text-[9px] text-slate-500">Care-Oriented</p></div>
                      <div className="text-center"><p className="text-xl font-black text-pink-400"><AnimatedCounter value={data.stats.diversity_pct} suffix="%" decimals={1} /></p><p className="text-[9px] text-slate-500">Diverse Founders</p></div>
                      <div className="text-center"><p className="text-xl font-black text-cyan-400"><AnimatedCounter value={data.stats.research_papers || 0} /></p><p className="text-[9px] text-slate-500">Research Papers</p></div>
                      <div className="text-center"><p className="text-xl font-black text-violet-400"><AnimatedCounter value={data.stats.countries || 0} /></p><p className="text-[9px] text-slate-500">Countries</p></div>
                    </div>
                  )}
                </div>
              </Reveal>

              {/* Section Tabs */}
              <Reveal delay={100}>
                <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-hide">
                  {sectionTabs.map(tab => {
                    const Icon = tab.icon;
                    const isActive = activeSection === tab.id;
                    return (
                      <button key={tab.id} onClick={() => setActiveSection(tab.id)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider whitespace-nowrap transition-all duration-300"
                        style={{
                          background: isActive ? "rgba(16,185,129,0.12)" : "rgba(255,255,255,0.02)",
                          color: isActive ? "#34d399" : "#64748b",
                          border: isActive ? "1px solid rgba(16,185,129,0.25)" : "1px solid transparent",
                        }}>
                        <Icon className="w-3 h-3" />
                        {tab.label}
                        {tab.count > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[8px]" style={{ background: isActive ? "rgba(16,185,129,0.2)" : "rgba(255,255,255,0.05)" }}>{tab.count}</span>}
                      </button>
                    );
                  })}
                </div>
              </Reveal>

              {/* Top 10 Focus Areas */}
              {activeSection === "areas" && data.top_10_focus_areas?.length > 0 && (
                <div className="space-y-2">
                  {data.top_10_focus_areas.map((area, i) => {
                    const ImpactIcon = IMPACT_ICONS[area.impact_type] || Zap;
                    const isExpanded = expandedArea === i;
                    return (
                      <Reveal key={area.area || `focus-${i}`} delay={i * 60}>
                        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden hover:border-white/[0.15] transition-all duration-300"
                          data-testid={`focus-area-${i}`}>
                          <button onClick={() => setExpandedArea(isExpanded ? null : i)}
                            className="w-full flex items-center gap-3 p-4 text-left group">
                            <ConfidenceGauge value={area.confidence} delay={i * 80} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="text-sm font-bold text-white group-hover:text-emerald-300 transition-colors">{area.area}</h4>
                                <span className="text-[8px] px-2 py-0.5 rounded-full font-bold uppercase"
                                  style={{ background: `${MATURITY_COLORS[area.maturity] || "#f59e0b"}15`, color: MATURITY_COLORS[area.maturity] || "#f59e0b" }}>
                                  {area.maturity}
                                </span>
                                <ImpactIcon className="w-3 h-3 text-slate-500" />
                              </div>
                              <p className="text-[10px] text-slate-400 mt-1 line-clamp-1">{area.rationale}</p>
                            </div>
                            <div className="transition-transform duration-300" style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0)" }}>
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            </div>
                          </button>
                          <div className="overflow-hidden transition-all duration-500" style={{ maxHeight: isExpanded ? "500px" : "0", opacity: isExpanded ? 1 : 0 }}>
                            <div className="px-4 pb-4 pt-0 space-y-3 border-t border-white/[0.04]">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                <div className="bg-emerald-500/[0.04] border border-emerald-500/10 rounded-lg p-3">
                                  <div className="flex items-center gap-1.5 mb-1.5">
                                    <BarChart3 className="w-3 h-3 text-emerald-400" />
                                    <p className="text-[9px] font-bold text-emerald-400 uppercase tracking-wider">Our Observatory Data</p>
                                  </div>
                                  <p className="text-[11px] text-slate-300 leading-relaxed">{area.our_data}</p>
                                </div>
                                <div className="bg-blue-500/[0.04] border border-blue-500/10 rounded-lg p-3">
                                  <div className="flex items-center gap-1.5 mb-1.5">
                                    <Eye className="w-3 h-3 text-blue-400" />
                                    <p className="text-[9px] font-bold text-blue-400 uppercase tracking-wider">Industry Report Signals</p>
                                  </div>
                                  <p className="text-[11px] text-slate-300 leading-relaxed">{area.report_signals}</p>
                                </div>
                              </div>
                              {area.example_products?.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {area.example_products.map((p, j) => (
                                    <span key={`prod-${p}-${j}`} className="text-[10px] px-2.5 py-1 bg-white/[0.04] border border-white/[0.08] rounded-full text-slate-300 hover:border-emerald-500/30 hover:text-emerald-300 transition-colors cursor-default">{p}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </Reveal>
                    );
                  })}
                </div>
              )}

              {/* White Spaces */}
              {activeSection === "spaces" && data.white_spaces?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.white_spaces.map((ws, i) => (
                    <Reveal key={ws.gap || `ws-${i}`} delay={i * 80}>
                      <div className="bg-amber-500/[0.04] border border-amber-500/12 rounded-xl p-4 hover:border-amber-500/25 transition-all duration-300 group" data-testid={`white-space-${i}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-amber-400 group-hover:animate-pulse" />
                            <h4 className="text-xs font-bold text-white">{ws.gap}</h4>
                          </div>
                          <span className="text-[8px] px-2 py-0.5 rounded-full font-bold uppercase flex-shrink-0"
                            style={{ background: ws.potential_impact === "high" ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)", color: ws.potential_impact === "high" ? "#ef4444" : "#f59e0b" }}>
                            {ws.potential_impact} potential
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-400 leading-relaxed">{ws.opportunity}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              )}

              {/* Convergence */}
              {activeSection === "convergence" && data.convergence_opportunities?.length > 0 && (
                <div className="space-y-3">
                  {data.convergence_opportunities.map((co, i) => (
                    <Reveal key={co.title || `conv-${i}`} delay={i * 100}>
                      <div className="bg-violet-500/[0.04] border border-violet-500/12 rounded-xl p-4 hover:border-violet-500/25 transition-all duration-300" data-testid={`convergence-${i}`}>
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {co.industries?.map((ind, j) => (
                            <span key={j} className="text-[10px] px-2.5 py-1 bg-violet-500/10 border border-violet-500/20 rounded-full text-violet-300 font-bold">
                              {ind}
                            </span>
                          ))}
                          {co.industries?.length > 1 && (
                            <div className="flex items-center gap-1">
                              <ArrowRight className="w-3 h-3 text-violet-400/50" />
                              <Sparkles className="w-3 h-3 text-violet-400 animate-pulse" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-slate-300 leading-relaxed">{co.opportunity}</p>
                        {co.example && <p className="text-[10px] text-violet-400/70 mt-2 italic border-l-2 border-violet-500/20 pl-3">{co.example}</p>}
                      </div>
                    </Reveal>
                  ))}
                </div>
              )}

              {/* Risk Signals */}
              {activeSection === "risks" && data.risk_signals?.length > 0 && (
                <div className="space-y-2">
                  {data.risk_signals.map((rs, i) => (
                    <Reveal key={rs.risk || `risk-${i}`} delay={i * 80}>
                      <div className="flex items-start gap-3 bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 hover:border-red-500/15 transition-all duration-300">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: `${SEVERITY_COLORS[rs.severity]}10` }}>
                          <AlertTriangle className="w-4 h-4" style={{ color: SEVERITY_COLORS[rs.severity] }} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-xs font-bold text-white">{rs.risk}</p>
                            <span className="text-[8px] px-1.5 py-0.5 rounded font-bold uppercase"
                              style={{ color: SEVERITY_COLORS[rs.severity], background: `${SEVERITY_COLORS[rs.severity]}10` }}>{rs.severity}</span>
                          </div>
                          <p className="text-[10px] text-slate-400">{rs.mitigation}</p>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              )}

              {/* Report References */}
              {activeSection === "reports" && data.report_references?.length > 0 && (
                <div className="space-y-3">
                  {data.report_references.map((ref, i) => (
                    <Reveal key={ref.source || `ref-${i}`} delay={i * 100}>
                      <div className="border-l-2 border-cyan-500/30 pl-4 py-2 hover:border-cyan-400/60 transition-colors duration-300">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="w-3 h-3 text-cyan-400" />
                          <p className="text-xs font-bold text-white">{ref.source}</p>
                        </div>
                        <p className="text-[10px] text-slate-300 mt-0.5">{ref.finding}</p>
                        <p className="text-[10px] text-cyan-400/60 mt-1 italic">{ref.relevance}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              )}

              {/* Outlook */}
              {data.outlook && (
                <Reveal delay={200}>
                  <div className="bg-gradient-to-r from-emerald-500/[0.06] to-blue-500/[0.06] border border-emerald-500/12 rounded-xl p-5 relative overflow-hidden">
                    <div className="absolute bottom-0 left-0 w-40 h-40 bg-emerald-500/[0.03] rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl" />
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                      <p className="text-[10px] font-bold text-emerald-400/70 uppercase tracking-[0.2em]">2-5 Year Outlook</p>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">{data.outlook}</p>
                  </div>
                </Reveal>
              )}
            </div>
          )}

          {data?.error && <p className="text-sm text-red-400 mt-3" data-testid="futures-error">{data.error}</p>}
        </div>
      </div>
    </Reveal>
  );
};


/* ─── Main Export ─── */
export const IndustryFutures = () => {
  const [matrixData, setMatrixData] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(true);
  const [dist, setDist] = useState(null);
  const [autoIndustry, setAutoIndustry] = useState(null);
  const compassRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      const [m, d] = await Promise.allSettled([
        axios.get(`${API}/industry/futures/matrix`),
        axios.get(`${API}/industry/distribution`),
      ]);
      if (m.status === "fulfilled") setMatrixData(m.value.data);
      if (d.status === "fulfilled") setDist(d.value.data);
      setMatrixLoading(false);
    };
    load();
  }, []);

  const handleSelectIndustry = useCallback((industry) => {
    setAutoIndustry(industry);
    setTimeout(() => {
      compassRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  }, []);

  const industries = dist?.distribution || [];

  return (
    <div className="space-y-6" data-testid="industry-futures">
      {/* Hero */}
      <Reveal>
        <div className="relative overflow-hidden rounded-2xl py-10 px-6 md:px-10 text-center"
          style={{ background: "linear-gradient(135deg, #0a1628 0%, #0c0a1e 40%, #0f1629 100%)", border: "1px solid rgba(16,185,129,0.15)" }}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-32 bg-emerald-500/[0.04] rounded-full blur-3xl" />
          <p className="text-[10px] font-bold text-emerald-400/70 uppercase tracking-[0.25em] mb-3">Industry Futures Engine</p>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">Where is each industry heading with AI?</h1>
          <p className="text-sm text-slate-400 max-w-2xl mx-auto">
            Combining ground-truth data from <span className="text-emerald-400 font-bold">{dist?.total?.toLocaleString() || "69,000+"}</span> AI entities with industry reports from McKinsey, Gartner, BCG, and the World Economic Forum.
          </p>
        </div>
      </Reveal>

      {/* Matrix */}
      {matrixLoading ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
          <p className="text-[10px] text-slate-500">Loading readiness matrix...</p>
        </div>
      ) : (
        <ReadinessMatrix matrixData={matrixData} onSelectIndustry={handleSelectIndustry} />
      )}

      {/* Compass */}
      <FuturesCompass industries={industries} compassRef={compassRef} autoIndustry={autoIndustry} />

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 0.3; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1.2); } }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .animate-fade-in { animation: fadeIn 0.5s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};
